import {
  doAdminMutation,
  doAdminQueries,
  IQueryInfo,
} from '../../../providers/mutations'
import { IMasterCurricula } from 'infrastructure/api'
import { useTreeGetter } from '../../../reducers/trees_hooks'
import {
  getGradeId,
  getSubjectId,
  nodeAttribution,
} from '../../utility/TreeHelpers'
import { useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  allPartnersByName,
  allProductsWithPrimaryTreeAtom,
} from '../../../api/masters.atoms'

export async function addProductsToGroup(
  { id: group_id }: { id: number },
  product_ids: number[],
) {
  const query: IQueryInfo = {
    resourceData: { key: 'addProductToGroup' },
    params: { group_id, product_ids },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.addProductToGroup, errors }
}

export async function removeProductFromGroup(
  group: { id: number },
  productId: number,
) {
  const query: IQueryInfo = {
    resourceData: { key: 'removeProductFromGroup' },
    params: { group_id: group.id, product_id: productId },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.removeProductFromGroup, errors }
}

export function useProductParams() {
  const getter = useTreeGetter()
  const partners = useAtomValue(allPartnersByName)
  return useCallback(
    async (masterData: IMasterCurricula) => {
      const { version: versionId, treeId, title } = masterData
      const { tree } = await getter({ versionId, treeId })
      if (!tree)
        return {
          error: 'Missing Tree',
        }
      const attribution = nodeAttribution(tree) ?? ''
      const partner = partners[attribution]
      const grade_id = getGradeId(tree)
      const subject_id = getSubjectId(tree)
      if (!partner) return { error: `Missing partner: ${attribution}` }
      if (!grade_id) return { error: `Missing grade_id: ${attribution}` }
      if (!subject_id) return { error: `Missing subject_id: ${attribution}` }
      return {
        params: {
          resourceData: { key: 'createProduct' },
          params: {
            title,
            grade_id,
            subject_id,
            primary_tree_tree_id: treeId,
            partner_id: partner.id,
          },
        },
      }
    },
    [partners],
  )
}

export function useSwapEditableTreeWithRefresh() {
  const productRefresh = useSetAtom(allProductsWithPrimaryTreeAtom)
  return useCallback(
    async (
      productId: number,
      treeIds: { treeId: string; versionId: string },
    ) => {
      await swapEditableTree(productId, treeIds)
      productRefresh()
    },
    [productRefresh],
  )
}

async function swapEditableTree(
  product_id: number,
  {
    treeId: tree_id,
    versionId: version_id,
  }: { treeId: string; versionId: string },
) {
  return doAdminMutation(
    {
      key: 'swapProductEditableTree',
    },
    {
      product_id,
      tree_id,
      version_id,
    },
  )
}

export async function markProductAsAssignable({ id }: { id: number }) {
  const query: IQueryInfo = {
    resourceData: { key: 'markProductAsAssignable' },
    params: { id },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.markProductAsAssignable, errors }
}

export async function markProductAsUnassignable({ id }: { id: number }) {
  const query: IQueryInfo = {
    resourceData: { key: 'markProductAsUnassignable' },
    params: { id },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.markProductAsUnassignable, errors }
}
